import React, { memo, useCallback, useMemo } from 'react'
import Select from 'react-select'
import { useTheme } from '@material-ui/core/styles'

import useStyles from './selectComponentStyle'
import { useTranslation } from 'react-i18next'
import Tooltip from '../display/tooltip'
import { useSelector } from 'react-redux'

const SelectComponent = (props) => {
  const theme = useTheme()
  const classes = useStyles()
  const { t } = useTranslation()

  const activeConversation = useSelector((state) => state.activeConversation)
  const [selectedOption, setSelectedOption] = React.useState(null)

  const handleChange = (_selectedOption) => {
    if (_selectedOption?.value === selectedOption?.value) return

    setSelectedOption(_selectedOption ? { ..._selectedOption } : null)
    props?.onChange({
      target: {
        name: props.name,
        value: _selectedOption?.value,
      },
    })
  }

  const options = useMemo(() => {
    return props.data?.map((el) => {
      return {
        value: el._id,
        label: el.name,
      }
    })
  }, [props?.data])

  useMemo(() => {
    if (!options || options?.length === 0) {
      handleChange(null)
      return
    }
    const value = options?.find((option) => option.value === props?.value)

    if (value) {
      handleChange(value)
      return
    }

    if (!props.disableAutoselect && options?.length === 1) {
      handleChange(options[0])
      return
    }

    handleChange(null)
  }, [options, props?.value, activeConversation])

  const backgroundColor = useCallback(
    (state) => {
      if (state.isSelected) {
        return theme.palette.primary.main
      }
      if (state.isFocused) {
        return theme.palette.hover3
      }
      return theme.palette.background.paper
    },
    [theme]
  )

  const styles = useMemo(() => {
    return {
      menu: (provided) => ({
        ...provided,
        zIndex: 1300,
      }),
      menuPortal: (provided) => ({
        ...provided,
        zIndex: 1300,
      }),
      menuList: (base) => ({
        ...base,
        backgroundColor: theme.palette.background.paper,
        '::-webkit-scrollbar': {
          width: theme.shape.scrollSize,
        },
        '::-webkit-scrollbar-thumb': {
          backgroundColor: theme.palette.scroll,
          borderRadius: theme.shape.scrollRadius,
        },
      }),
      control: (base) => ({
        ...base,
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.background.paper,
        borderColor: props?.error
          ? theme.palette.error.main
          : theme.palette.borderColors.main,
        padding: theme.spacing(1, 0),
        '&:hover': {
          borderColor: theme.palette.text.primary,
        },
        '&:focus-within': {
          borderColor: theme.palette.primary.main,
          '& .css-tlfecz-indicatorContainer': {
            color: theme.palette.primary.main,
          },
        },
        '& .css-tlfecz-indicatorContainer': {
          color: props?.error
            ? theme.palette.error.main
            : theme.palette.text.primary,
        },
      }),
      option: (provided, state) => ({
        ...provided,
        color: theme.palette.text.primary,
        backgroundColor: backgroundColor(state),
        wordBreak: 'break-word',
        '&:hover': {
          backgroundColor: state.isSelected
            ? theme.palette.primary.main
            : theme.palette.hover3,
        },
      }),
      singleValue: (provided, state) => ({
        ...provided,
        opacity: state.isDisabled ? 0.5 : 1,
        transition: 'opacity 300ms',
        zIndex: 'inherit',
        color: theme.palette.text.primary,
      }),
      input: (provided) => ({
        ...provided,
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.background.paper,
      }),
      placeholder: (provided) => ({
        ...provided,
        color: theme.palette.text.secondary,
        fontSize: theme.typography.body1.fontSize,
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        zIndex: 200,
      }),
      valueContainer: (provided) => ({
        ...provided,
        padding: '0px 10px',
        width: '100%',
        height: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        zIndex: 200,
      }),
      indicatorSeparator: (provided) => ({
        ...provided,
        display: 'none',
      }),
    }
  }, [theme])

  return (
    <div key={props?.key} className='select-component'>
      <Tooltip
        title={selectedOption?.label?.length > 40 && selectedOption?.label}
        placement="right"
      >
        <div className={classes.root}>
          <label
            className={`labelInput ${props?.error && 'error'}`}
            id={`select-label-${props?.name}`}
          >
            {props?.label}
          </label>
          <Select
            id={`select-label-${props?.name}`}
            name={props?.name}
            inputRef={props?.inputRef}
            displayEmpty
            value={selectedOption}
            defaultValue={props?.value}
            onChange={handleChange}
            disabled={props?.disabled}
            options={options}
            placeholder={props?.placeholder}
            className={classes.content}
            menuPortalTarget={props?.isRefDocument ? document.body : null}
            styles={styles}
            error={props?.error}
            noOptionsMessage={() => t('commons.searchResultEmpty')}
            isDisabled={props?.disabled}
          />
          {props?.error && (
            <spam className={classes.error}>{props?.helperText}</spam>
          )}
        </div>
      </Tooltip>
    </div>
  )
}

export default memo(SelectComponent)
